import { useMemo, useState, useEffect } from "react";
import { contrastRatio, wcagGrades } from "@/lib/wcag";
import { BRAND_KIT_CONTRAST } from "@/constants/brandKitTestIds";

/**
 * Color Contrast Checker — pairs any two ColorAssets in the brand's palette
 * and shows their WCAG 2.1 ratio + AA/AAA pass/fail. Pure-frontend; the
 * compliance engine can later promote a chosen pairing to a real rule.
 */
export default function ContrastChecker({ colors }) {
  const ids = useMemo(() => colors.map((c) => c.color_id), [colors]);
  const [aId, setAId] = useState(null);
  const [bId, setBId] = useState(null);

  // Smart defaults: prefer a headline/body/text color paired with a background.
  // Re-runs whenever the palette changes so a deletion doesn't leave a stale
  // (or duplicated) selection.
  useEffect(() => {
    if (colors.length < 2) return;
    const find = (role) => colors.find((c) => (c.role_tags || []).includes(role));
    const fg = find("headline") || find("text") || find("body_copy") || colors[0];
    const bg =
      find("background") ||
      colors.find((c) => c.color_id !== fg.color_id) ||
      colors[1];

    setAId((prev) => (prev && ids.includes(prev) ? prev : fg.color_id));
    setBId((prev) => {
      // Keep prev if still present AND not equal to the current foreground.
      const currentFg = (aId && ids.includes(aId)) ? aId : fg.color_id;
      if (prev && ids.includes(prev) && prev !== currentFg) return prev;
      if (bg.color_id !== currentFg) return bg.color_id;
      // Last resort: pick anything that isn't the foreground.
      const alt = colors.find((c) => c.color_id !== currentFg);
      return alt ? alt.color_id : prev;
    });
  }, [colors, ids, aId]);

  if (colors.length < 2) return null;

  const a = colors.find((c) => c.color_id === aId) || colors[0];
  const b = colors.find((c) => c.color_id === bId) || colors[1];
  const ratio = contrastRatio(a.hex, b.hex);
  const grades = wcagGrades(ratio);

  return (
    <section
      data-testid={BRAND_KIT_CONTRAST.root}
      className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-8"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-5">
        <div>
          <h3 className="font-serif text-xl leading-tight">Contrast checker</h3>
          <p className="text-xs text-[#6B6358] mt-1 max-w-xl">
            Pair any two colors and see whether their contrast clears WCAG&nbsp;2.1
            AA &amp; AAA for normal and large text. Advisory only — the M4
            compliance engine can be promoted to enforce a chosen pairing as a rule.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#B5462F]">
          WCAG&nbsp;2.1
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
        <ColorPicker
          label="Foreground (text / mark)"
          colors={colors}
          value={a.color_id}
          onChange={setAId}
          testid={BRAND_KIT_CONTRAST.foregroundSelect}
        />
        <button
          type="button"
          data-testid={BRAND_KIT_CONTRAST.swapButton}
          onClick={() => { setAId(b.color_id); setBId(a.color_id); }}
          className="px-3 py-2 text-xs rounded-full border border-[#1A1F2C]/20 text-[#6B6358] hover:border-[#1A1F2C]/40 md:mb-2 self-center"
          aria-label="Swap foreground and background"
        >
          ⇄
        </button>
        <ColorPicker
          label="Background"
          colors={colors}
          value={b.color_id}
          onChange={setBId}
          testid={BRAND_KIT_CONTRAST.backgroundSelect}
        />
      </div>

      <Preview a={a} b={b} ratio={ratio} grades={grades} />
    </section>
  );
}

function ColorPicker({ label, colors, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="w-8 h-8 rounded-md border border-[#1A1F2C]/20 shrink-0"
          style={{ backgroundColor: colors.find((c) => c.color_id === value)?.hex || "#fff" }}
        />
        <select
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
        >
          {colors.map((c) => (
            <option key={c.color_id} value={c.color_id}>
              {`${c.name} · ${c.hex}`}
            </option>
          ))}
        </select>
      </div>
    </label>
  );
}

function Preview({ a, b, ratio, grades }) {
  if (ratio == null || !grades) {
    return (
      <p className="text-sm text-[#B5462F] mt-5">
        Invalid HEX on one of the selected colors.
      </p>
    );
  }
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
      <div
        data-testid={BRAND_KIT_CONTRAST.preview}
        className="rounded-md p-6 border border-[#1A1F2C]/10"
        style={{ backgroundColor: b.hex, color: a.hex }}
      >
        <div className="font-serif text-3xl leading-tight">Large heading sample</div>
        <p className="text-sm mt-3 leading-relaxed">
          The quick brown fox jumps over the lazy dog. Local marketers in
          Charlotte, NC can pick this color pair for headlines, callouts, and
          body copy with confidence.
        </p>
        <p className="text-xs mt-3 opacity-80">
          Foreground {a.hex} · Background {b.hex}
        </p>
      </div>
      <div className="min-w-[14rem]">
        <div
          data-testid={BRAND_KIT_CONTRAST.ratio}
          className="text-5xl font-serif tracking-tight"
        >
          {ratio.toFixed(2)}
          <span className="text-base text-[#6B6358] ml-1">: 1</span>
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          <Grade label="AA · normal text"  pass={grades.aaNormal}  testid={BRAND_KIT_CONTRAST.aaNormal}  />
          <Grade label="AA · large text"   pass={grades.aaLarge}   testid={BRAND_KIT_CONTRAST.aaLarge}   />
          <Grade label="AAA · normal text" pass={grades.aaaNormal} testid={BRAND_KIT_CONTRAST.aaaNormal} />
          <Grade label="AAA · large text"  pass={grades.aaaLarge}  testid={BRAND_KIT_CONTRAST.aaaLarge}  />
        </div>
      </div>
    </div>
  );
}

function Grade({ label, pass, testid }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={testid} data-pass={pass}>
      <span className="text-[#3D3A35]">{label}</span>
      <span
        className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${
          pass
            ? "bg-[#1F6B6B]/10 text-[#1F6B6B]"
            : "bg-[#B5462F]/10 text-[#B5462F]"
        }`}
      >
        {pass ? "Pass" : "Fail"}
      </span>
    </div>
  );
}
